import { useEffect, useState } from "react";

import { IWard } from "../../../../@types/admin/AdminWard";
import { useUpdateWard } from "../../../../hooks/mutations/admin/wards";
import { useHospitalOptions } from "../../../../hooks/queries/admin/selectValues";
import { useToast } from "../../../../hooks/useToast";
import { errorToastMessage } from "../../../../utils/HttpError";
import {
  CheckboxField,
  FieldError,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  FormGrid,
  SectionActions,
  SectionCaption,
  SectionCard,
  SectionHeader,
  SectionHeading,
  SectionTitle,
} from "../AdminPage.styles";
import { AdminSelectField } from "../AdminSelectField";

interface IWardFormProps {
  ward: IWard;
}

interface IWardFormState {
  code: string;
  name: string;
  groupId: string;
  publish: boolean;
}

function toFormState(ward: IWard): IWardFormState {
  return {
    code: ward.code ?? "",
    name: ward.name ?? "",
    groupId: ward.groupId ?? "",
    publish: ward.publish,
  };
}

/** The API caps both at 70 characters (AddWardRequestDto/UpdateWardRequestDto). */
const MAX_TEXT_LENGTH = 70;

/**
 * Ward record form — Retool's `formWard` in edit mode (EN-5151). Edits the
 * ward itself only: code, name, hospital and publish.
 *
 * The ward's feature configuration rides along on the same response and the
 * same update DTO, but is deliberately not touched here. `UpdateWardAsync`
 * leaves the configuration alone only while **no** config field is present in
 * the payload — the configuration is edited by `WardConfigurationForm`
 * through its own PUT /Retool/Wards/{id}/Configuration (EN-5195), and ward
 * measurement types by Ward Measurement Types (EN-5157).
 */
export function WardForm({ ward }: IWardFormProps) {
  const { showToast } = useToast();
  const { hospitals, isLoading: hospitalsLoading } = useHospitalOptions();
  const { updateWard, isUpdatingWard } = useUpdateWard();

  const [form, setForm] = useState<IWardFormState>(() => toFormState(ward));

  useEffect(() => {
    setForm(toFormState(ward));
  }, [ward]);

  const errors = {
    code: form.code.trim() ? undefined : "Code is required.",
    name: form.name.trim() ? undefined : "Name is required.",
    groupId: form.groupId ? undefined : "Select a hospital.",
  };
  const hasErrors = Object.values(errors).some(Boolean);

  const handleSave = async () => {
    if (hasErrors) return;

    try {
      await updateWard({
        id: Number(ward.id),
        code: form.code.trim(),
        name: form.name.trim(),
        groupId: Number(form.groupId),
        publish: form.publish,
      });
      showToast("The ward was updated successfully.", "success");
    } catch (error) {
      showToast(
        errorToastMessage(error, "There was an error updating this ward."),
        "warning"
      );
    }
  };

  return (
    <SectionCard>
      <SectionHeader>
        <SectionHeading>
          <SectionTitle>Ward details</SectionTitle>
          <SectionCaption>ID {ward.id}</SectionCaption>
        </SectionHeading>
      </SectionHeader>

      <FormGrid>
        <FilterGroup>
          <FilterLabel htmlFor="ward-code">Code</FilterLabel>
          <FilterInput
            id="ward-code"
            type="text"
            maxLength={MAX_TEXT_LENGTH}
            value={form.code}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, code: e.target.value }))
            }
          />
          {errors.code && <FieldError>{errors.code}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="ward-name">Name</FilterLabel>
          <FilterInput
            id="ward-name"
            type="text"
            maxLength={MAX_TEXT_LENGTH}
            value={form.name}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, name: e.target.value }))
            }
          />
          {errors.name && <FieldError>{errors.name}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="ward-hospital">Hospital</FilterLabel>
          <AdminSelectField
            id="ward-hospital"
            value={form.groupId}
            disabled={hospitalsLoading}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, groupId: e.target.value }))
            }
          >
            <option value="">
              {hospitalsLoading ? "Loading..." : "Select an option"}
            </option>
            {hospitals.map((hospital) => (
              <option key={hospital.id} value={hospital.id}>
                {hospital.name}
              </option>
            ))}
          </AdminSelectField>
          {errors.groupId && <FieldError>{errors.groupId}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Visibility</FilterLabel>
          <CheckboxField>
            <input
              type="checkbox"
              checked={form.publish}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, publish: e.target.checked }))
              }
            />
            Published
          </CheckboxField>
        </FilterGroup>
      </FormGrid>

      <SectionActions>
        <FilterButton
          variant="primary"
          disabled={isUpdatingWard || hasErrors}
          onClick={handleSave}
        >
          {isUpdatingWard ? "Saving..." : "Save"}
        </FilterButton>
      </SectionActions>
    </SectionCard>
  );
}
